/**
 * PRODUCT_DEV-543(改): プリセットの「情報種別」(subjectType) と、種別ごとの
 * フィールド構成ルールを定義する純粋モジュール（DDB/React/Zod 非依存）。
 *
 * 種別:
 * - company    : 企業情報を取る商談 → `company` と `contacts` を必須。`customer_info` は不可。
 * - individual : 個人情報を取る商談 → `customer_info`（+ 氏名相当のサブフィールド1つ）を必須。`company`/`contacts` は不可。
 * - none       : いずれでもない → `company`/`contacts`/`customer_info` のいずれも設定不可。
 *
 * 背景(PD-543 QA): プリセットに `contacts` と `customer_info` が両方あると、お客様情報が空で
 * 担当者にデータが入る等の混在事故が起きた。種別で相互排他を強制してこれを防ぐ。
 *
 * PD-561: 個人種別の必須サブフィールドは「氏名（name 同義）1つ」に緩和（それ以外は推奨）。
 * 照合は氏名の同義キー（customerInfo の KEY_SYNONYMS.name と一致）で行うため、テナント独自 id
 * （"Name"/"氏名" 等）でも通る。氏名は個人検索の照合キーなので必須に残す。
 *
 * NOTE(PD-561 buildfix): 同義判定はこのファイル内に自己完結で持つ。customerInfo.ts を import すると
 * admin-web の strict コンパイル（noUncheckedIndexedAccess）に customerInfo.ts が引き込まれ、
 * 既存の index-access が型エラーになるため。氏名同義語は customerInfo.ts の KEY_SYNONYMS.name と
 * 一致させて保守する（下記 NAME_SUBFIELD_SYNONYMS）。
 *
 * 後方互換: `subjectType` 未設定の既存プリセットはこの検証の対象外（呼び出し側で skip）。
 */

export const PRESET_SUBJECT_TYPES = ["company", "individual", "none"] as const;
export type PresetSubjectType = (typeof PRESET_SUBJECT_TYPES)[number];

/**
 * 氏名（name）の同義キー。customerInfo.ts の `KEY_SYNONYMS.name` と同一内容に保つこと。
 * customer_info サブフィールド id がこのいずれか（正規化一致）なら「氏名相当」と見なす。
 */
const NAME_SUBFIELD_SYNONYMS = [
  "name",
  "氏名",
  "名前",
  "お名前",
  "フルネーム",
  "顧客名",
  "氏名漢字",
] as const;

/** キー正規化（customerInfo.ts の normKey と同一規約：全角英数→半角・空白/記号除去・小文字化）。 */
const normalizeSubfieldKey = (k: string): string =>
  k
    .replace(/[！-～]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0))
    .replace(/[\s_()（）・:：]/g, "")
    .toLowerCase();

/** サブフィールド id が「氏名（name 同義）」を指すか（テナント独自 id "Name"/"氏名" 等も認識）。 */
const isNameSubfieldId = (id: string): boolean => {
  const nk = normalizeSubfieldKey(id);
  return NAME_SUBFIELD_SYNONYMS.some((s) => normalizeSubfieldKey(s) === nk);
};

/** 個人商談のトリガー兼お客様情報フィールドの id（web individualProfile / api detect と同一規約）。 */
export const CUSTOMER_INFO_FIELD_ID = "customer_info";
/** 企業情報フィールドの id。 */
export const COMPANY_FIELD_ID = "company";
export const CONTACTS_FIELD_ID = "contacts";

/**
 * customer_info の標準サブフィールド定義（field-library の既定 + 個人種別の必須セット）。
 * id は `customerInfo.ts` の KEY_SYNONYMS が認識するキー（name/nameKana/occupation/...）と一致させ、
 * 手入力値・名寄せ反映が決定的に拾えるようにする。
 */
export const CUSTOMER_INFO_SUBFIELD_DEFS: ReadonlyArray<{
  id: string;
  label: string;
  description: string;
}> = [
  { id: "name", label: "氏名", description: "お客様の氏名" },
  { id: "nameKana", label: "ふりがな", description: "お客様の氏名の読み仮名（カタカナ）" },
  { id: "occupation", label: "職業", description: "職業・勤務先での役割（任意）" },
  { id: "traits", label: "特徴", description: "趣味・嗜好・性格・人物像など（任意）" },
  { id: "age", label: "年齢", description: "推定年齢（登録時点。任意）" },
  { id: "income", label: "収入", description: "収入（登録時点。任意）" },
  { id: "family", label: "家族構成", description: "家族構成（任意）" },
];

/**
 * customer_info の標準（推奨）サブフィールド id 一式。
 * PD-561: 個人種別の**必須**は氏名（name 同義）1つのみに緩和したため、この配列は
 * 「推奨セット」（admin の『推奨サブフィールドを補う』が追加する候補）を表す。
 */
export const CUSTOMER_INFO_RECOMMENDED_SUBFIELD_IDS: ReadonlyArray<string> =
  CUSTOMER_INFO_SUBFIELD_DEFS.map((s) => s.id);

/**
 * @deprecated PD-561 で必須は氏名1つに緩和。名称の互換のため残置（中身は推奨セット全件）。
 * 新規参照は {@link CUSTOMER_INFO_RECOMMENDED_SUBFIELD_IDS} を使うこと。
 */
export const CUSTOMER_INFO_REQUIRED_SUBFIELD_IDS: ReadonlyArray<string> =
  CUSTOMER_INFO_RECOMMENDED_SUBFIELD_IDS;

/** フィールド参照の最小形（DDBFieldReference / Patch/Create 入力いずれも満たす）。 */
export interface PresetFieldRefLike {
  id?: string;
  $ref?: string;
  subFields?: Array<{ id: string }>;
}

/** 参照の実効 id（inline id を優先、無ければ $ref 末尾セグメント）。 */
export const effectivePresetFieldId = (
  f: PresetFieldRefLike,
): string | undefined => {
  if (f?.id) return f.id;
  if (f?.$ref) return f.$ref.split(".").pop() || undefined;
  return undefined;
};

/** サブフィールドの最小形（表示ラベルと有効フラグを含む）。 */
export interface PresetSubFieldLike {
  id: string;
  label?: string;
  enabled?: boolean;
}

/**
 * PRODUCT_DEV-655(第9弾): preset から customer_info の有効なサブフィールド定義を取り出す。
 *
 * 名寄せ確定（`meetings/[meetingId]/patch.ts`）が、テナント独自サブフィールドの
 * **表示ラベル**を使って個人マスタの otherNotes へ「ラベル: 値」行を書くために使う。
 * web 側（Recording）は商談の meetingFields から同じ subFields を得ており、
 * サーバ側では preset が唯一の定義元になる。
 *
 * - `$ref` 参照のフィールドも `effectivePresetFieldId` で解決する。
 * - 無効化（enabled === false）されたフィールド／サブフィールドは除外する。
 */
export const findCustomerInfoSubFields = (
  preset:
    | {
        fields?: ReadonlyArray<
          Omit<PresetFieldRefLike, "subFields"> & {
            enabled?: boolean;
            subFields?: ReadonlyArray<PresetSubFieldLike>;
          }
        >;
      }
    | undefined,
): PresetSubFieldLike[] => {
  const field = (preset?.fields ?? []).find(
    (f) =>
      effectivePresetFieldId({ id: f.id, $ref: f.$ref }) ===
        CUSTOMER_INFO_FIELD_ID && f.enabled !== false,
  );
  return (field?.subFields ?? []).filter(
    (s) => s.enabled !== false && (s.id ?? "") !== "",
  );
};

export interface PresetSubjectValidationResult {
  ok: boolean;
  /** 必須なのに不足しているフィールド id。 */
  missingFieldIds: string[];
  /** 種別上設定できないのに存在するフィールド id。 */
  disallowedFieldIds: string[];
  /** 個人種別で customer_info に不足しているサブフィールド id。 */
  missingSubFieldIds: string[];
}

const okResult = (): PresetSubjectValidationResult => ({
  ok: true,
  missingFieldIds: [],
  disallowedFieldIds: [],
  missingSubFieldIds: [],
});

/**
 * 種別ごとのフィールド構成ルールを検証する。
 * `subjectType` 未設定（undefined）は後方互換のため常に ok（検証スキップ）。
 */
export function validatePresetSubjectFields(
  subjectType: PresetSubjectType | undefined,
  fields: ReadonlyArray<PresetFieldRefLike> | undefined,
): PresetSubjectValidationResult {
  if (!subjectType) return okResult();
  const list = fields ?? [];
  const ids = new Set(
    list
      .map(effectivePresetFieldId)
      .filter((x): x is string => Boolean(x)),
  );
  const has = (id: string): boolean => ids.has(id);

  const missingFieldIds: string[] = [];
  const disallowedFieldIds: string[] = [];
  const missingSubFieldIds: string[] = [];

  if (subjectType === "company") {
    if (!has(COMPANY_FIELD_ID)) missingFieldIds.push(COMPANY_FIELD_ID);
    if (!has(CONTACTS_FIELD_ID)) missingFieldIds.push(CONTACTS_FIELD_ID);
    if (has(CUSTOMER_INFO_FIELD_ID))
      disallowedFieldIds.push(CUSTOMER_INFO_FIELD_ID);
  } else if (subjectType === "individual") {
    if (!has(CUSTOMER_INFO_FIELD_ID)) {
      missingFieldIds.push(CUSTOMER_INFO_FIELD_ID);
    } else {
      // PD-561: 必須は「氏名（name 同義）1つ」のみ。テナント独自 id（"Name"/"氏名" 等）も
      // customerInfo の同義規約で氏名として認識する（ランタイムと同一照合）。
      const ci = list.find(
        (f) => effectivePresetFieldId(f) === CUSTOMER_INFO_FIELD_ID,
      );
      const hasNameSubField = (ci?.subFields ?? []).some((s) =>
        isNameSubfieldId(s.id),
      );
      if (!hasNameSubField) missingSubFieldIds.push("name");
    }
    if (has(COMPANY_FIELD_ID)) disallowedFieldIds.push(COMPANY_FIELD_ID);
    if (has(CONTACTS_FIELD_ID)) disallowedFieldIds.push(CONTACTS_FIELD_ID);
  } else {
    // none: 3 種いずれも設定不可
    for (const id of [
      COMPANY_FIELD_ID,
      CONTACTS_FIELD_ID,
      CUSTOMER_INFO_FIELD_ID,
    ]) {
      if (has(id)) disallowedFieldIds.push(id);
    }
  }

  return {
    ok:
      missingFieldIds.length === 0 &&
      disallowedFieldIds.length === 0 &&
      missingSubFieldIds.length === 0,
    missingFieldIds,
    disallowedFieldIds,
    missingSubFieldIds,
  };
}

/**
 * PRODUCT_DEV-684(追補): 識別項目の顔ぶれから情報種別を推定する。
 *
 * AI 作成のプリセットで種別が付かないと、個人検索（＝同じお客様の登録済み情報を
 * 引き当てて聞き直しを止める機能）が有効にできない。文脈判断そのものは LLM の仕事だが、
 * **出力に種別が無かったときの最終的な担保**としてここで決める。
 *
 * ⚠ **推定できないときは `undefined` を返し、決して `"none"` を返さない。**
 * 識別項目が無いのは「相手が居ない」ではなく「LLM が入れ忘れた」可能性があり、
 * `"none"` を確定させると 3 種の識別項目が設定不可になって個人検索が永久に効かなくなる。
 * 未設定なら後から編集画面で選べる（`validatePresetSubjectFields` は undefined を素通しする）。
 */
export function inferPresetSubjectTypeFromFields(
  fields: ReadonlyArray<PresetFieldRefLike> | undefined,
): PresetSubjectType | undefined {
  const ids = new Set(
    (fields ?? [])
      .map(effectivePresetFieldId)
      .filter((x): x is string => Boolean(x)),
  );
  const hasCompanySide = ids.has(COMPANY_FIELD_ID) || ids.has(CONTACTS_FIELD_ID);
  const hasIndividualSide = ids.has(CUSTOMER_INFO_FIELD_ID);
  // 混在は「どちらを消すか」に意図が要るので推定しない（呼び出し側が質問へ回す）。
  if (hasCompanySide && hasIndividualSide) return undefined;
  if (hasCompanySide) return "company";
  if (hasIndividualSide) return "individual";
  return undefined;
}

/** 種別の表示名。 */
export const presetSubjectTypeLabel = (t: PresetSubjectType): string =>
  t === "company" ? "企業" : t === "individual" ? "個人" : "いずれでもない";

/**
 * 検証結果を日本語エラーメッセージに整形する（admin UI / API 400 で共有）。
 * ok のときは空文字。
 */
export function formatPresetSubjectError(
  subjectType: PresetSubjectType,
  result: PresetSubjectValidationResult,
): string {
  if (result.ok) return "";
  const parts: string[] = [];
  if (result.missingFieldIds.length > 0) {
    parts.push(
      `必須フィールドが不足しています: ${result.missingFieldIds
        .map(presetFieldLabel)
        .join("・")}`,
    );
  }
  if (result.missingSubFieldIds.length > 0) {
    // PD-561: 個人種別の必須サブフィールドは氏名相当1つのみ。
    parts.push(
      `お客様情報に氏名（に相当する）サブフィールドが必要です`,
    );
  }
  if (result.disallowedFieldIds.length > 0) {
    parts.push(
      `この情報種別では設定できないフィールドがあります: ${result.disallowedFieldIds
        .map(presetFieldLabel)
        .join("・")}`,
    );
  }
  return `情報種別「${presetSubjectTypeLabel(subjectType)}」: ${parts.join(" / ")}`;
}

/** 表示用ラベル（エラーメッセージ整形用）。未知 id はそのまま返す。 */
export const presetFieldLabel = (id: string): string => {
  if (id === COMPANY_FIELD_ID) return "法人名";
  if (id === CONTACTS_FIELD_ID) return "担当者";
  if (id === CUSTOMER_INFO_FIELD_ID) return "お客様情報";
  const sub = CUSTOMER_INFO_SUBFIELD_DEFS.find((s) => s.id === id);
  return sub ? sub.label : id;
};

/* =====================================================================
 * PRODUCT_DEV-561: プリセットの「検索モード」排他ルール（正本）
 *
 * dynamicContextSettings.enableCompanySearch（企業検索）と
 * dynamicContextSettings.enableIndividualSearch（個人検索）は**同時 ON 不可**。
 * さらに個人検索は情報種別=個人（customer_info を持つ）プリセットでのみ有効。
 * 純粋関数（DDB/Zod/React 非依存）で admin API と UI 双方から使う。
 * ===================================================================== */

/** UI の3択ラジオに対応する検索モード。 */
export type SearchMode = "company" | "individual" | "none";

/** dynamicContextSettings の検索フラグ最小形。 */
export interface SearchModeFlags {
  enableCompanySearch?: boolean;
  enableIndividualSearch?: boolean;
}

/**
 * 2 bool から表示用の検索モードを導出する。
 * - enableIndividualSearch === true → individual
 * - enableCompanySearch === false（明示 OFF） → none
 * - それ以外（未設定含む・既定 true） → company（既存挙動）
 */
export const deriveSearchMode = (flags: SearchModeFlags): SearchMode => {
  if (flags.enableIndividualSearch === true) return "individual";
  if (flags.enableCompanySearch === false) return "none";
  return "company";
};

/** 検索モード → 2 bool（UI ラジオ保存時）。 */
export const searchModeToFlags = (
  mode: SearchMode,
): { enableCompanySearch: boolean; enableIndividualSearch: boolean } => ({
  enableCompanySearch: mode === "company",
  enableIndividualSearch: mode === "individual",
});

/** 用語辞書も含めた検索まわりの設定一式（`dynamicContextSettings` の最小形）。 */
export interface DynamicContextSettingsLike extends SearchModeFlags {
  enableDictionary?: boolean;
}

/**
 * PRODUCT_DEV-684(追補): 情報種別から録音時の検索モードを決める **正本**。
 *
 * 編集画面の情報種別 select（`admin/Presets/[presetId]` の onChange）と、AI 作成の
 * `repairDraftPreset` が同じ規則を使うためにここへ集約する。片方だけ直る事故を防ぐ。
 *
 * - `individual` / `company` → その種別に合う検索モードを**強制**する。
 *   個人プリセットで企業検索を残す構成に意味は無く、逆（個人検索を立てる）は要望そのもの。
 * - `none` → `enableIndividualSearch` だけ落とす。**`enableCompanySearch` は据え置く**
 *   （既定 true を false へ倒すと既存 `none` プリセットの録音時挙動が変わってしまう）。
 * - 種別が決まっていない（undefined）→ **何もしない**。
 * - `enableDictionary` は検索モードと直交するので常に素通し（意図的な OFF を消さない）。
 * - `current` が undefined でも種別が決まっていれば**オブジェクトを実体化して返す**。
 *   「選択された状態にしたい」という要望は、既定値任せ（未設定）ではなく
 *   明示値が保存されて初めて満たされる。
 */
export function alignSearchModeToSubject(
  current: DynamicContextSettingsLike | undefined,
  subjectType: PresetSubjectType | undefined | null,
): {
  settings: DynamicContextSettingsLike | undefined;
  changed: boolean;
  mode?: SearchMode;
} {
  if (!subjectType) return { settings: current, changed: false };

  const dictionary = current?.enableDictionary ?? true;
  if (subjectType === "none") {
    if (current?.enableIndividualSearch !== true) {
      return { settings: current, changed: false, mode: undefined };
    }
    return {
      settings: { ...current, enableIndividualSearch: false },
      changed: true,
      mode: deriveSearchMode({ ...current, enableIndividualSearch: false }),
    };
  }

  const mode: SearchMode = subjectType === "individual" ? "individual" : "company";
  const settings: DynamicContextSettingsLike = {
    ...current,
    ...searchModeToFlags(mode),
    enableDictionary: dictionary,
  };
  const changed =
    current === undefined ||
    current.enableCompanySearch !== settings.enableCompanySearch ||
    current.enableIndividualSearch !== settings.enableIndividualSearch;
  return { settings, changed, mode };
}

export interface SearchModeValidationResult {
  ok: boolean;
  /** 企業検索と個人検索が同時に ON。 */
  bothEnabled: boolean;
  /** 個人検索が ON なのに情報種別が個人でない。 */
  individualWithoutIndividualSubject: boolean;
}

/**
 * 検索モードの排他・整合を検証する。
 * - 企業検索と個人検索の同時 ON は不可。
 * - 個人検索 ON は subjectType === "individual" のときのみ許可。
 * 個人検索 OFF（既定）の既存プリセットは常に ok（後方互換）。
 */
export function validateSearchModeExclusivity(
  flags: SearchModeFlags,
  subjectType: PresetSubjectType | undefined | null,
): SearchModeValidationResult {
  const company = flags.enableCompanySearch !== false; // 既定 true
  const individual = flags.enableIndividualSearch === true; // 既定 false
  const bothEnabled = company && individual;
  const individualWithoutIndividualSubject =
    individual && subjectType !== "individual";
  return {
    ok: !bothEnabled && !individualWithoutIndividualSubject,
    bothEnabled,
    individualWithoutIndividualSubject,
  };
}

/** 検証結果を日本語エラーへ整形（ok は空文字）。 */
export function formatSearchModeError(
  result: SearchModeValidationResult,
): string {
  if (result.ok) return "";
  const parts: string[] = [];
  if (result.bothEnabled) {
    parts.push("企業検索と個人検索は同時に有効にできません（どちらか一方を選択してください）");
  }
  if (result.individualWithoutIndividualSubject) {
    parts.push("個人検索は情報種別「個人」のプリセットでのみ有効にできます");
  }
  return parts.join(" / ");
}
